/**
 * custom-variant-sync.js
 * Sothiares — variant ID integrity guard
 *
 * Purpose:
 *   Keep the product form's hidden `input[name="id"]` in sync with the size the
 *   customer has actually selected, SYNCHRONOUSLY.
 *
 * Why this exists:
 *   Atelier's product-form.js writes that hidden input in exactly one place —
 *   inside the .then() of the section-rendering fetch (product-form.js, the
 *   `#onVariantUpdate` handler). Until that network round-trip resolves, the
 *   radio button shows the new size but the form still holds the PREVIOUS
 *   variant ID. Measured on production over broadband: 800ms - 2300ms. On
 *   mobile data it is longer.
 *
 *   Two ways a customer gets the wrong size in their order:
 *     1. They click a size and hit Add to Cart inside that window.
 *     2. The fetch fails or is aborted. The theme's .catch() only console.errors,
 *        so the hidden input keeps the stale ID *indefinitely* while the UI
 *        shows the chosen size. Every subsequent Add to Cart is wrong.
 *
 *   Because the hidden input is seeded with
 *   `product.selected_or_first_available_variant.id`, the stale value on a fresh
 *   page load is the first variant — XS on every sized Sothiares product. That
 *   matches the customer reports of ordering M and receiving XS.
 *
 * Strategy — defence in depth, two independent guards:
 *   A. On `change` (capture phase, so we run BEFORE the theme's own handler):
 *      resolve the variant from the currently-checked options and write it
 *      immediately. No network involved.
 *   B. On `submit` (capture phase, before ProductFormComponent.handleSubmit):
 *      re-derive and re-write. This is the backstop — even if guard A never
 *      ran (script loaded late) or the theme's fetch failed, what gets POSTed
 *      to /cart/add.js is derived from the live DOM at the moment of submit.
 *
 *   Also repairs `?variant=` in the address bar. variant-picker.js gates that on
 *   `Theme.template.name === 'product'` (strict), but 46 of 47 Sothiares product
 *   templates use a suffix (e.g. 'product.linen-overshirt'), so the check is
 *   false site-wide and the URL never carries the selection. A reload — which
 *   iOS Safari does routinely to backgrounded tabs — then silently resets the
 *   customer to XS.
 *
 * Conventions:
 *   Custom file, `custom-` prefixed. Touches no Atelier core file. Uses
 *   delegated document-level listeners so it survives the theme's morph()
 *   re-render of the variant picker.
 *
 * Author: Sothiares dev
 * Date: 2026-08-12
 */
(function () {
  'use strict';

  var UNIT_SEP = '\u001f'; // ASCII unit separator; joining on '' would let ['XS','Navy'] collide with ['X','SNavy']

  /**
   * Resolved option-combination -> variant maps, keyed by product URL.
   * Populated ahead of time so click-time resolution stays synchronous.
   * @type {Map<string, Map<string, {id: number, available: boolean, inventory_management: string|null, inventory_policy: string, inventory_quantity: number}>>}
   */
  var variantMaps = new Map();
  /** @type {Map<string, Promise<any>>} in-flight loads, to dedupe */
  var pending = new Map();

  /**
   * Normalises an option-value list into a lookup key.
   * @param {Array<string|null|undefined>} values
   * @returns {string}
   */
  function optionKey(values) {
    return values
      .map(function (v) {
        return String(v == null ? '' : v).trim().toLowerCase();
      })
      .join(UNIT_SEP);
  }

  /**
   * @param {Element} picker
   * @returns {string} product URL without query string
   */
  function productUrlOf(picker) {
    var url = picker.getAttribute('data-product-url') || '';
    return url.split('?')[0];
  }

  /**
   * Loads and caches the full variant list for a picker's product.
   * Fire-and-forget: resolution falls back to data-variant-id if this never lands.
   * @param {Element} picker
   */
  function preloadVariantMap(picker) {
    var url = productUrlOf(picker);
    if (!url || variantMaps.has(url) || pending.has(url)) return;

    var request = fetch(url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (product) {
        var map = new Map();
        (product.variants || []).forEach(function (variant) {
          var opts = [variant.option1, variant.option2, variant.option3].filter(function (o) {
            return o != null;
          });
          map.set(optionKey(opts), variant);
        });
        variantMaps.set(url, map);
        // Options may already be selected (deep link, or a click that landed
        // before this resolved) — reconcile now that we can resolve properly.
        syncPicker(picker, null, { updateUrl: false });
      })
      .catch(function () {
        /* Fallback path handles this. Never block the customer on it. */
      })
      .then(function () {
        pending.delete(url);
      });

    pending.set(url, request);
  }

  /**
   * Reads the currently-selected option values in option order.
   * Only reads inside <variant-picker>, so the custom colour-swatch fieldset in
   * variant-main-picker.liquid (which sits outside the element and links to
   * separate products) is correctly ignored.
   * @param {Element} picker
   * @returns {string[]}
   */
  function selectedOptionValues(picker) {
    var values = [];
    var groups = picker.querySelectorAll('fieldset, .variant-option--dropdowns');

    Array.prototype.forEach.call(groups, function (group) {
      var checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        values.push(checked.value);
        return;
      }
      var select = group.querySelector('select');
      if (select) values.push(select.value);
    });

    return values;
  }

  /**
   * Resolves the variant ID for the picker's current selection.
   *
   * Prefers the full option-combination lookup. Falls back to the radio's own
   * data-variant-id, but ONLY for single-option products — on a multi-option
   * product the changed input's data-variant-id describes just that option's
   * first matching variant, not the full combination, so trusting it there
   * could write a genuinely wrong ID.
   *
   * @param {Element} picker
   * @param {Element|null} changedInput
   * @returns {string|null} variant ID, or null if it cannot be resolved safely
   */
  function resolveVariantId(picker, changedInput) {
    var map = variantMaps.get(productUrlOf(picker));

    if (map) {
      var variant = map.get(optionKey(selectedOptionValues(picker)));
      if (variant && variant.id) return String(variant.id);
    }

    var groupCount = picker.querySelectorAll('fieldset, .variant-option--dropdowns').length;
    if (groupCount > 1) return null;

    if (changedInput && changedInput.dataset && changedInput.dataset.variantId) {
      return changedInput.dataset.variantId;
    }

    var checked = picker.querySelector('input[type="radio"]:checked[data-variant-id]');
    if (checked) return checked.dataset.variantId;

    var option = picker.querySelector('select option[selected][data-variant-id]');
    return option ? option.getAttribute('data-variant-id') : null;
  }

  /**
   * Finds the product form(s) a given picker drives.
   * Mirrors ProductFormComponent's own scoping: nearest section or dialog.
   * @param {Element} picker
   * @returns {Element[]}
   */
  function formsFor(picker) {
    var scope = picker.closest('.shopify-section, dialog, quick-add-component') || document;
    var forms = Array.prototype.slice.call(scope.querySelectorAll('product-form-component'));
    var pickerProductId = picker.getAttribute('data-product-id');

    if (!pickerProductId) return forms;

    return forms.filter(function (form) {
      var formProductId = form.getAttribute('data-product-id');
      return !formProductId || formProductId === pickerProductId;
    });
  }

  /**
   * Writes a variant ID into a form's hidden input.
   * @param {Element} form
   * @param {string} variantId
   * @returns {boolean} whether the value changed
   */
  function writeVariantId(form, variantId) {
    var input = form.querySelector('input[name="id"]');
    if (!input || input.value === variantId) return false;
    input.value = variantId;
    return true;
  }

  /**
   * True when we're on a real product page (not a quick-add modal or card).
   * @param {Element} picker
   */
  function isMainProductPicker(picker) {
    if (!/\/products\//.test(window.location.pathname)) return false;
    return !picker.closest('quick-add-component, quick-add-dialog, product-card, .quick-add-modal');
  }

  /**
   * Syncs a picker's selection into its form(s), and optionally the URL.
   * @param {Element} picker
   * @param {Element|null} changedInput
   * @param {{updateUrl?: boolean}} [opts]
   */
  function syncPicker(picker, changedInput, opts) {
    var variantId = resolveVariantId(picker, changedInput);
    if (!variantId) return;

    formsFor(picker).forEach(function (form) {
      writeVariantId(form, variantId);
    });

    var updateUrl = !opts || opts.updateUrl !== false;
    if (!updateUrl || !isMainProductPicker(picker)) return;

    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('variant') === variantId) return;
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* URL rewriting is a nicety; never let it break add-to-cart. */
    }
  }

  /* -- Guard A: sync the instant an option changes ------------------------ */
  /* Capture phase so this runs before VariantPicker's own bubbling handler. */
  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var picker = target.closest('variant-picker');
      if (!picker) return;

      syncPicker(picker, target);
    },
    true
  );

  /* -- Guard B: re-derive at submit -------------------------------------- */
  /* The backstop. Runs before ProductFormComponent.handleSubmit builds its
     FormData, so whatever we write here is what actually reaches the cart. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.closest) return;

      var productForm = form.closest('product-form-component');
      if (!productForm) return;

      var scope = productForm.closest('.shopify-section, dialog, quick-add-component') || document;
      var productId = productForm.getAttribute('data-product-id');
      var pickers = Array.prototype.slice.call(scope.querySelectorAll('variant-picker'));

      var picker = pickers.filter(function (candidate) {
        var candidateId = candidate.getAttribute('data-product-id');
        return !productId || !candidateId || candidateId === productId;
      })[0];

      if (!picker) return;

      var variantId = resolveVariantId(picker, null);
      if (variantId) writeVariantId(productForm, variantId);
    },
    true
  );

  /* -- Preload variant data ---------------------------------------------- */
  function preloadAll() {
    var pickers = document.querySelectorAll('variant-picker[data-product-url]');
    Array.prototype.forEach.call(pickers, preloadVariantMap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadAll);
  } else {
    preloadAll();
  }

  // Quick-add modals and combined-listing morphs inject pickers after load.
  document.addEventListener('variant:update', preloadAll);
  document.addEventListener('dialog:open', preloadAll);
})();
